// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Company listing suppliers view route module.
 * Displays only companies with supplier role (currentView='suppliers').
 *
 * This route renders the company list with supplier role filtering,
 * showing only companies that are suppliers.
 */
import { PageContent } from '@/components/page-content';
import { crmSyncStatusSchema } from '@/constants/crm-sync-status';
import { sortDirectionSchema } from '@/constants/sort';
import { CustomerFilter } from '@/customer/api/get-customers';
import { CompanyList } from '@/customer/components/company-list';
import { customerSortPropertySchema } from '@/customer/hooks/use-customers';
import { searchTermSchema } from '@/utilities/search-term';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * Schema for validating URL search parameters.
 * Ensures type safety for route parameters.
 */
const searchSchema = z.object({
  query: searchTermSchema,
  archived: z.boolean().optional(),
  syncStatuses: z.array(crmSyncStatusSchema).optional(),
  direction: sortDirectionSchema.optional(),
  sort: customerSortPropertySchema.optional(),
  page: z.number().optional(),
  new: z.string().optional(),
});

/**
 * Company suppliers view route configuration.
 * Sets up the component and search parameter validation.
 */
export const Route = createFileRoute('/_app/companies/suppliers/')({
  component: CompaniesSuppliersIndexRoute,
  validateSearch: searchSchema,
});

/**
 * Company suppliers view route component.
 * Displays only companies with supplier role.
 */
function CompaniesSuppliersIndexRoute() {
  const search = Route.useSearch();

  /**
   * Filter configuration for showing only suppliers.
   * Applies supplier role filter (isSupplier=true).
   */
  const filter: CustomerFilter = {
    isArchived: search.archived ? 'all' : undefined,
    query: search.query,
    syncStatuses: search.syncStatuses,
    isSupplier: true,
  };

  return (
    <PageContent>
      <CompanyList currentView="suppliers" filter={filter} search={search} />
    </PageContent>
  );
}
